import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE_DISPLAY = "(424) 222-1975";
const PHONE_TEL = "tel:+14242221975";
const WHATSAPP_URL = "https://wa.me/14242221975";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      toast.success("Message sent", {
        description: "Kratos dispatch will reply to your email shortly.",
      });
    } catch (err) {
      toast.error("Could not send message", {
        description: "Please check the fields and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-[#f5f1e8] border border-[#cbc1a8] px-4 py-2.5 text-sm text-[#111111] focus:border-[#facc15] outline-none transition-colors";
  const labelCls = "block mono text-[11px] uppercase text-[#6b6350] mb-1";

  return (
    <div className="min-h-screen bg-[#f5f1e8] text-[#463f33]">
      <nav className="border-b border-[#ddd4bf] bg-[#f5f1e8]/95 backdrop-blur sticky top-0 z-50 px-6 py-5">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="contact-logo-link" className="flex items-center">
            <img src="/assets/kratos-logo.png" alt="Kratos Enterprise Logistics logo" className="h-[80px] w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="contact-back-link"
            className="mono text-xs uppercase tracking-wider text-[#6b6350] hover:text-[#b45309] transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
          </Link>
        </div>
      </nav>

      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 bg-[#fbf8f1] border border-[#ddd4bf] p-8">
            <div className="mono text-xs uppercase tracking-widest text-[#b45309] mb-2 flex items-center gap-2">
              <Mail className="w-3.5 h-3.5" /> Email Dispatch
            </div>
            <h1 className="editorial text-4xl text-[#111111] mb-6">WRITE TO KRATOS</h1>

            {sent ? (
              <div className="border border-[#b45309]/40 p-6" data-testid="enquiry-confirmation">
                <div className="mono text-xs uppercase tracking-widest text-[#b45309] mb-2">
                  Message Received
                </div>
                <p className="text-sm leading-relaxed mb-2">
                  Your message is with Kratos dispatch. Expect a reply at the email you provided.
                </p>
                <button
                  data-testid="enquiry-new-btn"
                  onClick={() => {
                    setSent(false);
                    setForm({ name: "", email: "", message: "" });
                  }}
                  className="mono text-[11px] uppercase tracking-wider text-[#6b6350] hover:text-[#b45309] transition-colors underline underline-offset-4"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form className="space-y-4" onSubmit={submit} data-testid="enquiry-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="name">Your Name / Company</label>
                    <input id="name" name="name" required value={form.name} onChange={onChange}
                      placeholder="Who should dispatch ask for" className={inputCls}
                      data-testid="enquiry-name-input" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="email">Your Email</label>
                    <input id="email" name="email" type="email" required value={form.email} onChange={onChange}
                      placeholder="Where dispatch can reply" className={inputCls}
                      data-testid="enquiry-email-input" />
                  </div>
                </div>
                <div>
                  <label className={labelCls} htmlFor="message">Message</label>
                  <textarea id="message" name="message" required rows={6} value={form.message} onChange={onChange}
                    placeholder="Routes, freight details, timelines — anything dispatch should know"
                    className={`${inputCls} resize-y`}
                    data-testid="enquiry-message-input" />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="enquiry-submit-btn"
                  className="w-full mono uppercase text-xs tracking-widest font-bold bg-[#facc15] text-black py-3.5 hover:bg-[#eab308] transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>

          <div className="lg:col-span-5 flex flex-col gap-4">
            <a href="mailto:dinobrega@kratostransportation.com" data-testid="contact-email-dinobrega"
              className="p-6 bg-[#fbf8f1] border border-[#ddd4bf] flex items-center gap-4 hover:border-[#b45309]/60 transition-colors group">
              <Mail className="w-5 h-5 text-[#b45309] shrink-0" />
              <div>
                <div className="editorial text-xl text-[#111111]">Di Nobrega</div>
                <div className="mono text-[11px] text-[#6b6350] group-hover:text-[#b45309] transition-colors break-all">
                  dinobrega@kratostransportation.com
                </div>
              </div>
            </a>
            <a href="mailto:luisarevalo@kratostransportation.com" data-testid="contact-email-luisarevalo"
              className="p-6 bg-[#fbf8f1] border border-[#ddd4bf] flex items-center gap-4 hover:border-[#b45309]/60 transition-colors group">
              <Mail className="w-5 h-5 text-[#b45309] shrink-0" />
              <div>
                <div className="editorial text-xl text-[#111111]">Luis Arevalo</div>
                <div className="mono text-[11px] text-[#6b6350] group-hover:text-[#b45309] transition-colors break-all">
                  luisarevalo@kratostransportation.com
                </div>
              </div>
            </a>
            <div className="p-6 bg-[#fbf8f1] border border-[#ddd4bf] flex items-center justify-between">
              <div>
                <span className="mono text-[10px] uppercase text-[#25D366] flex items-center gap-1.5">
                  <MessageCircle className="w-3 h-3" /> Direct Dispatch
                </span>
                <div className="editorial text-xl text-[#111111]">WhatsApp Line</div>
                <p className="text-xs text-[#6b6350]">Instant route assistance · {PHONE_DISPLAY}</p>
              </div>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="px-4 py-2 bg-[#25D366] text-black mono text-xs uppercase font-bold shrink-0 hover:bg-[#1fb857] transition-colors"
              >
                Chat
              </a>
            </div>
            <div className="p-6 bg-[#fbf8f1] border border-[#ddd4bf] flex items-center justify-between">
              <div>
                <span className="mono text-[10px] uppercase text-[#b45309] flex items-center gap-1.5">
                  <Phone className="w-3 h-3" /> Direct Line
                </span>
                <div className="editorial text-xl text-[#111111]">Voice Call</div>
                <p className="text-xs text-[#6b6350]">Speak directly with dispatch · {PHONE_DISPLAY}</p>
              </div>
              <a
                href={PHONE_TEL}
                data-testid="contact-call-btn"
                className="px-4 py-2 border border-[#facc15] text-[#b45309] mono text-xs uppercase font-bold shrink-0 hover:bg-[#facc15] hover:text-black transition-colors"
              >
                Call
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
